import Checkbox from "@mui/material/Checkbox";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useFavoriteStore, usePropertyStore } from "../../index";
import { FavoriteTargetType } from "../../interfaces/IFavorite";
import GeometryService from "../../services/geometryService";
import FavoriteEmptyIcon from "../icons/FavoriteEmptyIcon";
import FavoriteFilledIcon from "../icons/FavoriteFilledIcon";

const PropertyFavoriteCheckbox = observer(({ id, className }: IEntityFavoriteProps<string>) => {
    const propertyStore = usePropertyStore();
    const favoriteStore = useFavoriteStore();
    const geometryService = useMemo(() => new GeometryService(), []);
    const property = useMemo(() => computed(() => Array.from(propertyStore.properties.values()).find(p => p.id === id)!), [id, propertyStore.properties]).get();
    const favoritePropertyDetails = useMemo(() => computed(() => favoriteStore.favoritePropertiesDetails.find(fpd => fpd.shapes.some(shape => geometryService.isPointInsideShape(property.centerPoint, shape))) ?? null), [property]).get();
    const isFavorite = useMemo(() => computed(() => favoriteStore.favorites.some(f => f.targetId === favoritePropertyDetails?.id && f.targetType === FavoriteTargetType.Property)), [favoritePropertyDetails, favoriteStore.favorites]).get();

    return (
        <FavoriteCheckbox
            isFavorite={isFavorite}
            onAdd={() => favoriteStore.addFavoriteProperty(property)}
            onDelete={() => favoriteStore.deleteFavoriteProperty(favoritePropertyDetails!.id)}
            className={className}
        />
    );
});

const DealFavoriteCheckbox = observer(({ id, className }: IEntityFavoriteProps<number>) => {
    const favoriteStore = useFavoriteStore();
    const isFavorite = useMemo(() => computed(() => favoriteStore.favorites.some(f => f.targetId === id && f.targetType === FavoriteTargetType.Deal)), [id, favoriteStore.favorites]).get();

    return (
        <FavoriteCheckbox
            isFavorite={isFavorite}
            onAdd={() => favoriteStore.addFavoriteDeal(id)}
            onDelete={() => favoriteStore.deleteFavoriteDeal(id)}
            className={className}
        />
    );
});

const LeadFavoriteCheckbox = observer(({ id, className }: IEntityFavoriteProps<number>) => {
    const favoriteStore = useFavoriteStore();
    const isFavorite = useMemo(() => computed(() => favoriteStore.favorites.some(f => f.targetId === id && f.targetType === FavoriteTargetType.Lead)), [id, favoriteStore.favorites]).get();

    return (
        <FavoriteCheckbox
            isFavorite={isFavorite}
            onAdd={() => favoriteStore.addFavoriteLead(id)}
            onDelete={() => favoriteStore.deleteFavoriteLead(id)}
            className={className}
        />
    );
});

const LeaseCompFavoriteCheckbox = observer(({ id, className }: IEntityFavoriteProps<number>) => {
    const favoriteStore = useFavoriteStore();
    const isFavorite = useMemo(() => computed(() => favoriteStore.favorites.some(f => f.targetId === id && f.targetType === FavoriteTargetType.LeaseComp)), [id, favoriteStore.favorites]).get();

    return (
        <FavoriteCheckbox
            isFavorite={isFavorite}
            onAdd={() => favoriteStore.addFavoriteLeaseComp(id)}
            onDelete={() => favoriteStore.deleteFavoriteLeaseComp(id)}
            className={className}
        />
    );
});

const SaleCompFavoriteCheckbox = observer(({ id, className }: IEntityFavoriteProps<number>) => {
    const favoriteStore = useFavoriteStore();
    const isFavorite = useMemo(() => computed(() => favoriteStore.favorites.some(f => f.targetId === id && f.targetType === FavoriteTargetType.SaleComp)), [id, favoriteStore.favorites]).get();

    return (
        <FavoriteCheckbox
            isFavorite={isFavorite}
            onAdd={() => favoriteStore.addFavoriteSaleComp(id)}
            onDelete={() => favoriteStore.deleteFavoriteSaleComp(id)}
            className={className}
        />
    );
});

const TenantInMarketFavoriteCheckbox = observer(({ id, className }: IEntityFavoriteProps<number>) => {
    const favoriteStore = useFavoriteStore();
    const isFavorite = useMemo(() => computed(() => favoriteStore.favorites.some(f => f.targetId === id && f.targetType === FavoriteTargetType.TenantInMarket)), [id, favoriteStore.favorites]).get();

    return (
        <FavoriteCheckbox
            isFavorite={isFavorite}
            onAdd={() => favoriteStore.addFavoriteTenantInMarket(id)}
            onDelete={() => favoriteStore.deleteFavoriteTenantInMarket(id)}
            className={className}
        />
    );
});

const CompanyFavoriteCheckbox = observer(({ id, className }: IEntityFavoriteProps<number>) => {
    const favoriteStore = useFavoriteStore();
    const isFavorite = useMemo(() => computed(() => favoriteStore.favorites.some(f => f.targetId === id && f.targetType === FavoriteTargetType.Company)), [id, favoriteStore.favorites]).get();

    return (
        <FavoriteCheckbox
            isFavorite={isFavorite}
            onAdd={() => favoriteStore.addFavoriteCompany(id)}
            onDelete={() => favoriteStore.deleteFavoriteCompany(id)}
            className={className}
        />
    );
});

const FavoriteCheckbox = ({ isFavorite, onAdd, onDelete, className }: IFavoriteCheckboxProps) => {
    return (
        <span className={className}>
            <Checkbox
                checked={isFavorite}
                onChange={e => e.target.checked ? onAdd() : onDelete()}
                onClick={e => e.stopPropagation()}
                icon={<FavoriteEmptyIcon />}
                checkedIcon={<FavoriteFilledIcon />}
            />
        </span>
    );
}

interface IFavoriteCheckboxProps {
    isFavorite: boolean;
    onAdd: () => Promise<void>;
    onDelete: () => Promise<void>;
    className?: string;
}

interface IEntityFavoriteProps<T> {
    id: T;
    className?: string;
}

export {
    CompanyFavoriteCheckbox, DealFavoriteCheckbox,
    LeadFavoriteCheckbox,
    LeaseCompFavoriteCheckbox, PropertyFavoriteCheckbox, SaleCompFavoriteCheckbox,
    TenantInMarketFavoriteCheckbox
};

